import { m, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import type React from 'react';

import { Button } from '~/components/ui/Button';
import { Container } from '~/components/ui/Container';
import { FiberTexture } from '~/components/ui/FiberTexture';
import { HexPattern } from '~/components/ui/HexPattern';
import { Reveal } from '~/components/ui/Reveal';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

/**
 * Hero section — full viewport, Void Navy bg, massive display headline.
 * id="hero" anchors the scroll spy.
 */
function Hero(): React.ReactElement {
  const content = useContent();
  const { hero } = content;
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      id="hero"
      className={cn(
        'relative min-h-screen flex flex-col',
        'bg-[var(--color-void-navy)]',
        'overflow-hidden',
        'scroll-anchor'
      )}
      aria-labelledby="hero-heading"
    >
      {/* Decorative background layers — aria-hidden */}
      <FiberTexture className="absolute inset-0 w-full h-full" />
      <HexPattern className="absolute inset-0 w-full h-full opacity-40" />

      {/* Main content — flex-1 fills all space above stats row */}
      <Container className="relative z-10 flex flex-col flex-1 justify-center pt-40 pb-16">
        {/* Eyebrow */}
        <div className="mb-6">
          <span
            className={cn(
              'font-display font-semibold tracking-[0.22em] uppercase text-[0.6875rem]',
              'text-[var(--color-cyan-signal)]'
            )}
          >
            {hero.eyebrow}
          </span>
        </div>

        {/* Headline — massive display type */}
        <div className="relative mb-2">
          <h1
            id="hero-heading"
            className={cn(
              'text-[4.5rem] md:text-[6rem] lg:text-[8rem]',
              'leading-[0.92] font-display font-extrabold tracking-tight uppercase',
              'text-[var(--color-frost-white)]',
              'max-w-4xl'
            )}
          >
            {hero.headline}
          </h1>
          {/* Cyan accent underline bar — draw-in animation on mount */}
          <m.span
            aria-hidden="true"
            className="block mt-4 h-[3px] w-24 bg-[var(--color-cyan-signal)] rounded-full origin-left"
            initial={{
              scaleX: prefersReducedMotion ? 1 : 0,
              opacity: prefersReducedMotion ? 1 : 0,
            }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{
              duration: prefersReducedMotion ? 0 : 0.6,
              ease: [0.25, 0.1, 0.25, 1],
              delay: prefersReducedMotion ? 0 : 0.2,
            }}
          />
        </div>

        {/* Subhead */}
        <Reveal delay={100} className="mt-8 max-w-[640px]">
          <p
            className={cn(
              'text-lg md:text-xl font-body font-light leading-[1.8]',
              'text-[var(--color-steel-mist)]'
            )}
          >
            {hero.sub}
          </p>
        </Reveal>

        {/* CTA row */}
        <Reveal delay={200} className="mt-10 flex flex-wrap gap-4">
          <Button href="#contact" variant="primary" size="lg">
            {hero.ctaPrimary}
          </Button>
          <Button href="#process" variant="secondary" size="lg">
            {hero.ctaSecondary}
          </Button>
        </Reveal>
      </Container>

      {/* Stats row — pinned to bottom of viewport */}
      <div
        className={cn(
          'relative z-10 mt-auto',
          'border-t border-[rgba(232,236,244,0.08)]',
          'bg-[rgba(13,15,26,0.6)] backdrop-blur-sm'
        )}
      >
        <Container>
          <div className="grid grid-cols-3 divide-x divide-[rgba(232,236,244,0.1)]">
            {hero.stats.map((stat) => (
              <div key={stat.label} className="py-6 px-4 md:px-8 flex flex-col items-start gap-1">
                {/* Tiny dash eyebrow */}
                <span
                  aria-hidden="true"
                  className="block w-4 h-px bg-[var(--color-cyan-signal)] mb-1 opacity-60"
                />
                <span
                  className={cn(
                    'font-display font-bold tracking-tight',
                    'text-xl md:text-2xl',
                    'text-[var(--color-frost-white)]'
                  )}
                >
                  {stat.value}
                </span>
                <span
                  className={cn(
                    'text-xs font-body font-light tracking-wide',
                    'text-[var(--color-steel-mist)]'
                  )}
                >
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </Container>
      </div>

      {/* Scroll indicator — decorative */}
      <div
        aria-hidden="true"
        className={cn(
          'absolute bottom-24 right-8 md:right-12 z-10',
          'flex flex-col items-center gap-1',
          'text-[var(--color-steel-mist)]',
          'animate-bounce'
        )}
      >
        <ChevronDown size={20} strokeWidth={1.5} />
      </div>
    </section>
  );
}

export { Hero };
export default Hero;
